// React
import React, {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
// Helpers
import { compact, includes, first, size } from "@mefisto/utils";
// Framework
import { usePortal } from "stack/core";
import { useLocation } from "navigation";
import {
  classnames,
  makeStyles,
  Section,
  Spacer,
  Cloak,
  Grid,
  Tabs,
  Typography,
} from "ui";
// Options
import SceneOptions from "./components/SceneOptions";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
  },
  divider: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: theme.palette.border.surface.width,
    background: theme.palette.border.surface.color,
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const SceneTabs = ({
  context = "tabs",
  value,
  options,
  title,
  disableTabCloak,
  disableDivider,
  children,
}) => {
  // Framework
  const { navigation } = usePortal();
  const {
    hash: { tab },
  } = useLocation();
  // Styles
  const classes = useStyles();
  // Memo
  const tabs = useMemo(() => {
    return compact(
      Children.map(
        children,
        (child) => isValidElement(child) && child.props.value
      )
    );
  }, [children]);
  const currentTab = useMemo(() => {
    return includes(tabs, tab) ? tab : first(tabs);
  }, [tab, tabs]);
  const enableCloak = useMemo(() => {
    return !disableTabCloak && size(tabs) === 1;
  }, [disableTabCloak, tabs]);
  // Callbacks
  const handleChange = useCallback(
    (event, tab) => {
      navigation.setHash({
        params: { tab },
      });
    },
    [navigation]
  );
  // Render
  return (
    <Section context={context} value={value}>
      <div className={classes.root}>
        <Grid container alignItems="center">
          {!disableDivider && <div className={classes.divider} />}
          {title && (
            <Grid item>
              <Typography variant="h6">{title}</Typography>
            </Grid>
          )}
          <Grid item style={{ width: enableCloak ? 0 : "auto" }}>
            <Cloak enabled={enableCloak}>
              <Tabs
                value={currentTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={handleChange}
              >
                {Children.map(
                  children,
                  (child) =>
                    isValidElement(child) &&
                    cloneElement(child, {
                      __display: {
                        render: "tabs",
                      },
                    })
                )}
              </Tabs>
            </Cloak>
          </Grid>
          <Spacer />
          {options && (
            <Grid item flex>
              <SceneOptions options={options} />
            </Grid>
          )}
        </Grid>
      </div>
      {Children.map(
        children,
        (child) =>
          isValidElement(child) &&
          cloneElement(child, {
            __display: {
              render: "content",
              currentTab,
            },
          })
      )}
    </Section>
  );
};

SceneTabs.propTypes = {
  context: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.node,
  disableTabCloak: PropTypes.bool,
  disableDivider: PropTypes.bool,
  children: PropTypes.node,
};

export default SceneTabs;
